import React, { Component } from 'react'
import { Col, Row } from 'reactstrap'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import {
  Chip,
  Divider,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
} from '@mui/material'
import {
  LANGUAGES,
  SIGNING_OPTIONS,
  SUBJECT_OPTIONS,
  VALIDITY_OPTIONS,
  emptyTemplate,
} from '../utils/contracts'

/**
 * Create/edit form for a contract template.
 *
 * The terms and the covering note are deliberately PLAIN TEXT, edited in a multiline
 * TextField and previewed with white-space: pre-line - exactly as the old
 * RecurrentContractTerms editor did. The PDF is composed server-side from this text, and
 * feeding it HTML from a rich editor would corrupt every generated contract.
 */
class ContractTemplateEditor extends Component {
  constructor(props) {
    super(props)
    const source = props.template || {}
    const base = emptyTemplate()
    this.state = {
      template: {
        ...base,
        ...source,
        // Nested objects and arrays need fresh copies - the spread above is shallow and
        // this component mutates its own state object in place.
        terms: { ...base.terms, ...(source.terms || {}) },
        coveringnote: { ...base.coveringnote, ...(source.coveringnote || {}) },
        company: { ...base.company, ...(source.company || {}) },
        venues: [...(source.venues || [])],
      },
      lang: 'fi',
      saving: false,
    }
  }

  setPath = (property, value) => {
    const { template } = this.state
    const parts = property.split('.')
    let target = template
    for (let i = 0; i < parts.length - 1; i += 1) {
      target = target[parts[i]]
    }
    target[parts[parts.length - 1]] = value
    this.setState({ template })
  }

  handleTextChange = (e, property) => this.setPath(property, e.target.value)

  handleNumberChange = (e, property) => {
    const parsed = parseFloat(String(e.target.value).replace(',', '.').replace(/[^0-9.]/g, ''))
    this.setPath(property, isFinite(parsed) ? parsed : 0)
  }

  handleStateSwitch = (e) => this.setPath('state', e.target.checked ? 'active' : 'paused')

  // An empty venues list means "every venue", which is the common case - so the UI is a
  // set of toggles rather than a required selection.
  toggleVenue = (venueName) => {
    const { template } = this.state
    template.venues = template.venues.includes(venueName)
      ? template.venues.filter((v) => v !== venueName)
      : [...template.venues, venueName]
    this.setState({ template })
  }

  isValid = () => {
    const { template } = this.state
    return !!(template.name && template.name.trim().length > 0)
  }

  handleSave = async () => {
    if (!this.isValid()) return
    this.setState({ saving: true })
    await this.props.onSave(this.state.template)
    this.setState({ saving: false })
  }

  renderLanguageTabs = () => (
    <div>
      {LANGUAGES.map((language) => (
        <Chip
          key={language.value}
          label={language.label}
          clickable
          size='small'
          color='primary'
          variant={this.state.lang === language.value ? 'filled' : 'outlined'}
          onClick={() => this.setState({ lang: language.value })}
          style={{ marginRight: 4 }}
        />
      ))}
    </div>
  )

  render() {
    const { template, lang, saving } = this.state
    const venues = this.props.venues || []

    return (
      <div style={{ padding: 20, backgroundColor: 'white' }}>
        <Row>
          <Col>
            <Typography variant='h6'>{template.id ? 'Muokkaa sopimusmallia' : 'Lisää sopimusmalli'}</Typography>
          </Col>
          <Col xs='auto'>
            <Button onClick={this.props.onCancel} disabled={saving}>
              Palaa listaan
            </Button>
          </Col>
        </Row>

        <Row className='mt-4'>
          <Col xs='12' md='4'>
            <TextField
              fullWidth
              required
              size='small'
              label='Nimi'
              value={template.name}
              onChange={(e) => this.handleTextChange(e, 'name')}
              error={!this.isValid()}
              helperText={!this.isValid() ? 'Nimi on pakollinen' : 'Tämän admin valitsee varaukselle'}
            />
          </Col>
          <Col xs='12' md='4'>
            <TextField
              fullWidth
              size='small'
              label='Sopimustyyppi'
              value={template.contracttype}
              onChange={(e) => this.handleTextChange(e, 'contracttype')}
              helperText='Vapaa teksti — pelkkä merkintä listalle, ei ohjaa mitään'
            />
          </Col>
          <Col xs='12' md='2'>
            <TextField
              fullWidth
              size='small'
              label='Järjestys'
              value={template.order}
              onChange={(e) => this.handleNumberChange(e, 'order')}
            />
          </Col>
          <Col xs='12' md='2' className='mt-2'>
            <FormControlLabel
              control={<Switch size='small' checked={template.state === 'active'} onChange={this.handleStateSwitch} />}
              label={<span style={{ fontSize: 13 }}>{template.state === 'active' ? 'Käytössä' : 'Ei käytössä'}</span>}
            />
          </Col>
        </Row>

        <Row className='mt-3'>
          <Col xs='12' md='8'>
            <TextField
              fullWidth
              size='small'
              label='Kuvaus'
              value={template.description}
              onChange={(e) => this.handleTextChange(e, 'description')}
              helperText='Valinnainen ohjeteksti valitsimen alle'
            />
          </Col>
        </Row>

        <Divider sx={{ mt: 4, mb: 1 }} />
        <Typography variant='subtitle2' sx={{ mt: 2 }}>
          Käyttäytyminen
        </Typography>

        <Row className='mt-3'>
          <Col xs='12' md='3'>
            <TextField
              fullWidth
              select
              size='small'
              label='Voimassaolo'
              value={template.validity}
              onChange={(e) => this.handleTextChange(e, 'validity')}
              helperText='Määräaikainen ottaa päättymispäivän vakiovuoron loppupäivästä'
            >
              {VALIDITY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Col>
          <Col xs='12' md='3'>
            <TextField
              fullWidth
              select
              size='small'
              label='Allekirjoitus oletuksena'
              value={template.signingtype}
              onChange={(e) => this.handleTextChange(e, 'signingtype')}
              helperText='Voi vaihtaa varauskohtaisesti'
            >
              {SIGNING_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Col>
          <Col xs='12' md='3'>
            <TextField
              fullWidth
              select
              size='small'
              label='Käytetään'
              value={template.subjecttype}
              onChange={(e) => this.handleTextChange(e, 'subjecttype')}
            >
              {SUBJECT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Col>
          <Col xs='12' md='3'>
            <TextField
              fullWidth
              size='small'
              label='Vakio-osan PDF'
              value={template.standardpartfile}
              onChange={(e) => this.handleTextChange(e, 'standardpartfile')}
              helperText='Muuttumaton osa, johon ehdot liitetään'
            />
          </Col>
        </Row>

        {venues.length > 1 && (
          <Row className='mt-3'>
            <Col xs='12'>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                Toimipisteet — jos et valitse yhtään, malli on käytössä kaikissa.
              </Typography>
              {venues.map((venue) => (
                <Chip
                  key={venue}
                  label={venue}
                  clickable
                  size='small'
                  color='primary'
                  variant={template.venues.includes(venue) ? 'filled' : 'outlined'}
                  onClick={() => this.toggleVenue(venue)}
                  style={{ marginRight: 4 }}
                />
              ))}
            </Col>
          </Row>
        )}

        <Divider sx={{ mt: 4, mb: 1 }} />
        <Typography variant='subtitle2' sx={{ mt: 2 }}>
          Ehdot
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          VUOKRANANTAJA = halliyhtiö, VAKIOVUOROLAINEN = asiakas. Nämä termit määritellään sopimuksen
          vakio-osassa — käytä niitä viitataksesi osapuoliin.
        </Typography>

        <Row className='mt-3'>
          <Col xs='12'>{this.renderLanguageTabs()}</Col>
        </Row>

        <Row className='mt-3'>
          <Col xs='12' md='6'>
            <TextField
              fullWidth
              multiline
              minRows={10}
              size='small'
              label={'Sopimusehdot (' + lang + ')'}
              value={template.terms[lang] || ''}
              onChange={(e) => this.handleTextChange(e, 'terms.' + lang)}
            />
          </Col>
          <Col xs='12' md='6'>
            <Typography variant='caption' color='text.secondary'>
              Esikatselu
            </Typography>
            <div
              style={{
                whiteSpace: 'pre-line',
                border: '1px solid #e0e0e0',
                borderRadius: 4,
                padding: 8,
                minHeight: 220,
                fontSize: 13,
              }}
            >
              {template.terms[lang] || ''}
            </div>
          </Col>
        </Row>

        <Row className='mt-3'>
          <Col xs='12' md='6'>
            <TextField
              fullWidth
              multiline
              minRows={4}
              size='small'
              label={'Saateteksti sähköistä allekirjoitusta lähetettäessä (' + lang + ')'}
              value={template.coveringnote[lang] || ''}
              onChange={(e) => this.handleTextChange(e, 'coveringnote.' + lang)}
            />
          </Col>
        </Row>

        <Divider sx={{ mt: 4, mb: 1 }} />
        <Typography variant='subtitle2' sx={{ mt: 2 }}>
          Vuokranantaja
        </Typography>

        <Row className='mt-3'>
          <Col xs='12' md='4'>
            <TextField
              fullWidth
              size='small'
              label='Nimi'
              value={template.company.name}
              onChange={(e) => this.handleTextChange(e, 'company.name')}
            />
          </Col>
          <Col xs='12' md='4'>
            <TextField
              fullWidth
              size='small'
              label='Osoite'
              value={template.company.streetaddress}
              onChange={(e) => this.handleTextChange(e, 'company.streetaddress')}
            />
          </Col>
          <Col xs='12' md='4'>
            <TextField
              fullWidth
              size='small'
              label='Y-tunnus'
              value={template.company.businessid}
              onChange={(e) => this.handleTextChange(e, 'company.businessid')}
            />
          </Col>
        </Row>
        <Row className='mt-3'>
          <Col xs='12' md='4'>
            <TextField
              fullWidth
              size='small'
              label='Sähköposti'
              value={template.company.email}
              onChange={(e) => this.handleTextChange(e, 'company.email')}
            />
          </Col>
          <Col xs='12' md='4'>
            <TextField
              fullWidth
              size='small'
              label='Puhelin'
              value={template.company.phone}
              onChange={(e) => this.handleTextChange(e, 'company.phone')}
            />
          </Col>
        </Row>

        {template.legacytype && (
          <Alert severity='info' sx={{ mt: 3 }}>
            Tämä malli kirjoittaa sopimukseen vanhan tyypin <b>{template.legacytype}</b>, jotta nykyinen
            PDF-generointi toimii ennallaan. Kenttä poistuu kun taustajärjestelmä lukee sopimusmallin.
          </Alert>
        )}

        <Row className='mt-5'>
          <Col>
            <Button onClick={this.props.onCancel} disabled={saving}>
              Peruuta
            </Button>{' '}
            <Button variant='contained' color='primary' onClick={this.handleSave} disabled={saving || !this.isValid()}>
              {saving ? 'Tallennetaan...' : 'Tallenna'}
            </Button>
          </Col>
        </Row>
      </div>
    )
  }
}

export default ContractTemplateEditor
